import React, { useMemo, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Modal,
  Typography,
  Space,
  Card,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { sortBy } from 'lodash';
// Helpers
import { DeliverableV2, isContentTask } from 'helpers/deliverables';
// Types
import {
  CampaignSmPlatform,
  CampaignTaskType,
  CampaignMediaType,
  CreateCampaignTaskInput,
} from 'api/graphql-global-types';
// Styles
import styles from './DeliverablesChecklist.module.scss';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

export const DeliverableType = {
  Content: 'content',
  Post: 'post',
} as const;

export type DeliverableTypeValue =
  typeof DeliverableType[keyof typeof DeliverableType];

type DeliverableFormData = {
  mediaType?: CampaignMediaType | null;
  postType?: CampaignTaskType | null;
  title: string;
  description: string;
};

type DeliverablesChecklistProps = {
  deliverablesChecklist: DeliverableV2[];
  setDeliverablesChecklist: (
    updater: (prev: DeliverableV2[]) => DeliverableV2[]
  ) => void;
  platform: CampaignSmPlatform;
  error?: string | null;
};

const DeliverablesChecklist: React.FC<DeliverablesChecklistProps> = ({
  deliverablesChecklist,
  setDeliverablesChecklist,
  platform,
  error,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<DeliverableTypeValue | null>(
    null
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [step, setStep] = useState(1);
  const [form] = Form.useForm<DeliverableFormData>();

  const contentTaskOptions = useMemo(
    () => [
      { label: 'Image', value: CampaignMediaType.IMAGE },
      { label: 'Video', value: CampaignMediaType.VIDEO },
    ],
    []
  );

  const postTaskOptions = useMemo(() => {
    const isTikTok = platform === CampaignSmPlatform.TT;
    return isTikTok
      ? [{ label: 'Video', value: CampaignTaskType.TT_VIDEO }]
      : [
          { label: 'Live', value: CampaignTaskType.IG_LIVE },
          { label: 'Post', value: CampaignTaskType.IG_POST },
          { label: 'Reel', value: CampaignTaskType.IG_REEL },
          { label: 'Story', value: CampaignTaskType.IG_STORY },
        ];
  }, [platform]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedType(null);
    setStep(1);
    setEditingIndex(null);
    form.resetFields();
  };

  const handleEdit = (deliverable: DeliverableV2, originalIndex: number) => {
    setEditingIndex(originalIndex);
    if (deliverable.type === CampaignTaskType.CONTENT) {
      setSelectedType(DeliverableType.Content);
      form.setFieldsValue({
        title: deliverable.title ?? '',
        description: deliverable.description ?? '',
        ...(isContentTask(deliverable as any) && {
          mediaType: (deliverable as any).mediaType,
        }),
        postType: null,
      });
    } else {
      setSelectedType(DeliverableType.Post);
      form.setFieldsValue({
        title: deliverable.title ?? '',
        description: deliverable.description ?? '',
        postType: deliverable.type,
        mediaType: null,
      });
    }

    setStep(2);
    setIsModalOpen(true);
  };

  const handleSave = (values: DeliverableFormData) => {
    if (!selectedType) return;

    let newDeliverable: CreateCampaignTaskInput;

    if (selectedType === DeliverableType.Content) {
      if (!values.mediaType) return;
      newDeliverable = {
        type: CampaignTaskType.CONTENT,
        mediaType: values.mediaType,
        title: values.title,
        description: values.description,
      };
    } else {
      if (!values.postType) return;
      newDeliverable = {
        type: values.postType,
        title: values.title,
        description: values.description,
      };
    }

    setDeliverablesChecklist((prev) => {
      if (editingIndex !== null) {
        // Update existing deliverable and preserve orderIdx if it exists
        const updated = [...prev];
        const existingDeliverable = updated[editingIndex];

        if (
          'orderIdx' in existingDeliverable &&
          existingDeliverable.orderIdx !== null
        ) {
          (newDeliverable as any).orderIdx = existingDeliverable.orderIdx;
        }

        updated[editingIndex] = newDeliverable;
        return updated;
      } else {
        const maxOrderIdx = Math.max(
          ...prev.map((d) => ('orderIdx' in d ? d.orderIdx : 0) || 0),
          0
        );
        (newDeliverable as any).orderIdx = maxOrderIdx + 1;
        return [...prev, newDeliverable];
      }
    });

    form.resetFields();
    setIsModalOpen(false);
    setSelectedType(null);
    setStep(1);
    setEditingIndex(null);
  };

  const getPostTypeLabel = (deliverable: any) => {
    if (deliverable.type !== CampaignTaskType.CONTENT) {
      return postTaskOptions.find((opt) => opt.value === deliverable.type)
        ?.label;
    }
    return '';
  };

  const getPlatformLabel = (platform: CampaignSmPlatform) => {
    switch (platform) {
      case CampaignSmPlatform.IG:
        return 'Instagram';
      case CampaignSmPlatform.TT:
        return 'TikTok';
      case CampaignSmPlatform.TW:
        return 'X';
      case CampaignSmPlatform.FB:
        return 'Facebook';
      case CampaignSmPlatform.YT:
        return 'YouTube';
      default:
        return 'Social Media';
    }
  };

  const sortedDeliverables = useMemo(() => {
    return sortBy([...(deliverablesChecklist || [])], 'orderIdx').map(
      (deliverable, sortedIndex) => {
        const originalIndex = deliverablesChecklist.findIndex(
          (d) => d === deliverable
        );
        return { deliverable, originalIndex, sortedIndex };
      }
    );
  }, [deliverablesChecklist]);

  return (
    <div className={styles.root}>
      <Text className={styles.description}>
        Add all of the content creation and social posts required by talent for
        this campaign.
      </Text>

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.deliverablesList}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            setSelectedType(null);
            setStep(1);
            setIsModalOpen(true);
          }}
          style={{ marginBottom: 16 }}
        >
          Add Deliverable
        </Button>
        {sortedDeliverables.map(
          ({ deliverable, originalIndex, sortedIndex }) => {
            const postTypeLabel = getPostTypeLabel(deliverable);

            return (
              <div key={originalIndex} className={styles.deliverableItem}>
                <div className={styles.deliverableContent}>
                  <Text strong>
                    Deliverable {sortedIndex + 1} -{' '}
                    {deliverable.type === CampaignTaskType.CONTENT
                      ? 'Content'
                      : `Post - ${getPlatformLabel(platform)} ${
                          postTypeLabel ?? 'Social Post'
                        }`}
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    <Text>{deliverable.title}</Text>
                  </div>
                </div>
                <div className={styles.deliverableActions}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(deliverable, originalIndex)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setDeliverablesChecklist((prev) =>
                        prev.filter((_, i) => i !== originalIndex)
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              </div>
            );
          }
        )}
      </div>

      <Modal
        title="Deliverable Checklist"
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <div>
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <Space>
                <Button
                  type={step >= 1 ? 'primary' : 'default'}
                  shape="circle"
                  size="small"
                >
                  1
                </Button>
                <Text>-</Text>
                <Button
                  type={step >= 2 ? 'primary' : 'default'}
                  shape="circle"
                  size="small"
                >
                  2
                </Button>
              </Space>
            </div>
            <Text>
              Step {step}: {step === 1 ? 'Select Deliverable Type' : 'Details'}
            </Text>
          </div>

          {step === 1 && (
            <>
              <div className={styles.selectType}>
                <Title level={3}>Select Deliverable Type</Title>
                <Text>
                  Select the type of deliverable to add to your campaign.
                </Text>

                <div
                  className={styles.deliverableTypes}
                  style={{ marginTop: 16 }}
                >
                  <Card
                    hoverable
                    className={`${styles.button} ${
                      selectedType === DeliverableType.Content
                        ? styles.active
                        : ''
                    }`}
                    onClick={() => setSelectedType(DeliverableType.Content)}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedType === DeliverableType.Content
                          ? '2px solid #1890ff'
                          : '1px solid #d9d9d9',
                      marginBottom: 16,
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 48, marginBottom: 8 }}>
                        <span role="img" aria-label="camera">
                          📷
                        </span>
                      </div>
                      <Title level={4}>Content</Title>
                      <Text>
                        I need talent to make content (video or image).
                      </Text>
                    </div>
                  </Card>

                  <Card
                    hoverable
                    className={`${styles.button} ${
                      selectedType === DeliverableType.Post ? styles.active : ''
                    }`}
                    onClick={() => setSelectedType(DeliverableType.Post)}
                    style={{
                      cursor: 'pointer',
                      border:
                        selectedType === DeliverableType.Post
                          ? '2px solid #1890ff'
                          : '1px solid #d9d9d9',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 48, marginBottom: 8 }}>
                        <span role="img" aria-label="mobile phone">
                          📱
                        </span>
                      </div>
                      <Title level={4}>Social Post</Title>
                      <Text>I need talent to post on their socials.</Text>
                    </div>
                  </Card>
                </div>
              </div>

              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <Button
                  type="primary"
                  onClick={() => setStep(2)}
                  disabled={!selectedType}
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Form form={form} layout="vertical" onFinish={handleSave}>
                {selectedType === DeliverableType.Content && (
                  <Form.Item
                    name="mediaType"
                    label="Content Type"
                    rules={[
                      { required: true, message: 'Please select content type' },
                    ]}
                  >
                    <Select placeholder="Select Content Type">
                      {contentTaskOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {selectedType === DeliverableType.Post && (
                  <Form.Item
                    name="postType"
                    label="Post Type"
                    rules={[
                      { required: true, message: 'Please select post type' },
                    ]}
                  >
                    <Select placeholder="Select Post Type">
                      {postTaskOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                <Form.Item
                  name="title"
                  label="Content Title"
                  rules={[
                    { required: true, message: 'Please enter content title' },
                  ]}
                  extra={
                    selectedType === DeliverableType.Content
                      ? 'Only include one content item per deliverable.'
                      : 'Only include one post item per deliverable.'
                  }
                >
                  <Input
                    placeholder="Enter Content Title"
                    maxLength={120}
                    showCount
                  />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: 'Please enter description' },
                  ]}
                  extra="Describe what you're expecting the Talent to do for this deliverable."
                >
                  <TextArea
                    placeholder="Enter description"
                    maxLength={5000}
                    showCount
                    rows={4}
                  />
                </Form.Item>

                <div style={{ textAlign: 'right', marginTop: 24 }}>
                  <Space>
                    <Button onClick={() => setStep(1)}>Back</Button>
                    <Button type="primary" htmlType="submit">
                      Save
                    </Button>
                  </Space>
                </div>
              </Form>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DeliverablesChecklist;
